import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  IconButton,
  Avatar,
  TextField,
  Menu,
  MenuItem,
  Checkbox,
  Tooltip,
  Dialog,
  DialogTitle,
  Typography,
  DialogActions,
  Button,
  DialogContent,
  Radio,
  Chip,
} from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import { User_plus } from "../../../shared/assets/icons";
import CapitalizeFirstLetter from "../../../shared/utils/CapitalizeFirstLetter";
import GlobalButton from "../../../shared/utils/GlobalButton";

const KamDropdown = ({
  clientAddIndicationId,
  setClientAddIndicationId,
  kamdata = [],
  onChange,
  initialSelected = [],
  getRfpList,
  campaignModal = false,
  enquiryData,
}) => {
  const [selectedKam, setSelectedKam] = useState([]);
  const assigningRole = localStorage.getItem("role");

  const activeKamdata = useMemo(
    () => (campaignModal ? kamdata.filter((kam) => !kam?.isDeleted) : kamdata),
    [campaignModal, kamdata],
  );
  const assignableKamdata = useMemo(
    () =>
      campaignModal
        ? activeKamdata.filter((kam) => {
            const memberRole = String(kam.role);
            if (assigningRole === "-1") return true;
            if (assigningRole === "1")
              return ["0", "4", "7"].includes(memberRole);
            if (assigningRole === "0") return ["4", "7"].includes(memberRole);
            return false;
          })
        : kamdata.filter((kam) => kam.role != -1),
    [campaignModal, activeKamdata, assigningRole, kamdata],
  );

  const [kamForMail, setKAMForMail] = useState(
    null || enquiryData?.clientRepresentative,
  );
  const [loading, setLoading] = useState(false);
  const serverURL = process.env.REACT_APP_URL;
  const token = localStorage.getItem("token");

  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");
  const [searchForEmail, setSearchForEmail] = useState("");
  const [showPing, setShowPing] = useState(false);

  useEffect(() => {
    // Only show ping if this enquiry’s ID matches the one set in parent
    if (clientAddIndicationId && clientAddIndicationId === enquiryData?._id) {
      setShowPing(true);
      const timer = setTimeout(() => {
        setShowPing(false);
        setClientAddIndicationId(null); // reset after 3s
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [clientAddIndicationId, enquiryData?._id]);

  useEffect(() => {
    if (!initialSelected) {
      setSelectedKam([]);
      return;
    }

    let selectedObjs = [];
    if (typeof initialSelected[0] === "string") {
      selectedObjs = activeKamdata.filter((k) =>
        initialSelected.includes(k._id),
      );
    } else {
      selectedObjs = initialSelected.filter(
        (kam) => !campaignModal || !kam?.isDeleted,
      );
    }

    setSelectedKam(selectedObjs);
  }, [initialSelected, activeKamdata, campaignModal]);

  const onClose = () => {
    let selectedObjs = [];
    if (typeof initialSelected[0] === "string") {
      selectedObjs = activeKamdata.filter((k) =>
        initialSelected.includes(k._id),
      );
    } else {
      selectedObjs = initialSelected.filter(
        (kam) => !campaignModal || !kam?.isDeleted,
      );
    }

    setSelectedKam(selectedObjs);
    setAnchorEl(null);
  };

  const handleAssign = async () => {
    if (loading) return; // prevent multiple clicks
    const onlyCSM = selectedKam.filter((ele) => ele.role == 0);
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const keyAccountManagerId = selectedKam.map((s) => s._id);

      await axios.put(
        `${process.env.REACT_APP_URL}/requestForProposal/assignAdminRfp/${enquiryData._id}`,
        { keyAccountManagerId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      getRfpList();
    } catch (err) {
      console.error("Error assigning KAM:", err);
    }

    setLoading(true);
    try {
      const res = await axios.put(
        `${serverURL}/requestForProposal/editRfp/${enquiryData?._id}`,
        {
          emailAddress: enquiryData?.campaignOwnerId.emailAddress,
          sentQuotationMail: true,
          CSM: onlyCSM,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (res.data.success) {
        Swal.fire({
          customClass: {
            container: "my-swal",
          },

          title: "Success!",
          text: "CSMs have been assigned successfully, and the quotation email has been sent.",
          icon: "success",
        }).then(() => {
          onClose();
          getRfpList();
        });
      } else {
        Swal.fire("Error", "Failed to assign representative.", "error");
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Something went wrong while assigning.", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleKam = (kam) => {
    let newSelected;
    const exists = selectedKam.find((k) => k._id === kam._id);

    if (exists) {
      newSelected = selectedKam.filter((k) => k._id !== kam._id);
    } else {
      newSelected = [...selectedKam, kam];
    }

    setSelectedKam(newSelected);
    // if (onChange) onChange(newSelected);
  };

  const handleCampaignAssign = async (selectedKam) => {
    // ✅ Prevent multiple clicks
    if (loading) return;

    setLoading(true);
    try {
      if (onChange) await onChange(selectedKam);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const filteredKam = assignableKamdata.filter((kam) =>
    kam.name.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelect = (KamId) => {
    setKAMForMail(KamId._id);
  };

  return (
    <Box className="flex justify-center items-center">
      <Tooltip title="Add CSM">
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          className={showPing ? "relative" : ""}
        >
          {showPing && (
            <span className="absolute inline-flex h-full w-full rounded-full bg-blue-600 opacity-75 animate-ping"></span>
          )}
          <User_plus className="relative z-10" />
        </IconButton>
      </Tooltip>
      <Tooltip
        title={
          selectedKam.length > 0 ? (
            <div>
              {selectedKam.map((s, i) => (
                <div key={s?.id ?? i}>
                  {i + 1}. {s?.name}
                </div>
              ))}
            </div>
          ) : (
            "No KAM Assigned"
          )
        }
        arrow
      >
        <span style={{ display: "inline-block" }}>
          {selectedKam?.length > 0
            ? `${selectedKam[0]?.name}${
                selectedKam?.length > 1 ? ` +${selectedKam?.length - 1}` : ""
              }`
            : "Select KAM"}
        </span>
      </Tooltip>

      <Dialog
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setSearch("");
        }}
        anchorEl={anchorEl}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            height: { sm: "80vh" },
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            overflow: "hidden",
            backgroundColor: "#f7fafc",
          },
        }}
      >
        {/* Fixed Header */}
        <DialogTitle
          sx={{
            // backgroundColor: "#d9dadb",
            display: "flex",
            justifyContent: "space-between",
            alignContent: "center",
            flexDirection: "column",
          }}
        >
          <div>
            {/* <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
              Pixell Members
            </Typography> */}

            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
              Assign {campaignModal ? "members" : "CSM"} to{" "}
              {campaignModal ? "campaign" : "enquiry"}
            </Typography>
          </div>

          {!campaignModal && (
            <Box
              sx={{
                backgroundColor: "#fff",
                p: 1.5,
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                display: "inline-block",
              }}
            >
              {/* First line - label + name */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  backgroundColor: "#fff",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                  }}
                >
                  Enquiry Created By:
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: "#1976D2",
                  }}
                >
                  {enquiryData?.addedByAdmin?.name || "Not Available"}
                </Typography>
              </Box>

              {/* Second line - chip */}
              <Chip
                label={
                  enquiryData?.addedByAdmin?.role == -1
                    ? "Super Admin"
                    : enquiryData?.addedByAdmin?.role == 0
                      ? "CSM"
                      : "Team Member"
                }
                color={
                  enquiryData?.addedByAdmin?.role == 0
                    ? "error"
                    : enquiryData?.addedByAdmin?.role == -1
                      ? "primary"
                      : "default"
                }
                size="small"
                sx={{
                  fontWeight: 600,
                  borderRadius: "6px",
                  color: "#fff",
                  width: "fit-content",
                  mt: 0.8, // ✅ chip moves to new line
                  "&.MuiChip-colorDefault": {
                    backgroundColor: "#9e9e9e",
                    color: "#fff",
                  },
                }}
              />
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          <div className="flex flex-col md:flex-row justify-between items-start w-full md:h-[100%]  gap-3 ">
            <div
              className={`w-full border border-1 rounded-md p-3 shadow-sm  overflow-auto h-full  bg-white`}
            >
              <Box sx={{ py: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Find Member "
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Box>
              {filteredKam.length > 0 ? (
                <div className="px-3 h-[85%] overflow-auto">
                  {filteredKam
                    .filter((ele) => ele.role != -1)
                    .map((kam) => {
                      const isSelected = selectedKam?.some(
                        (k) => k?._id === kam?._id,
                      );
                      return (
                        <MenuItem
                          key={kam._id}
                          onClick={() => toggleKam(kam)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: 1,
                            backgroundColor: isSelected
                              ? "#E3F2FD"
                              : "transparent",
                            borderRadius: 2,
                            border: "1px solid var(--listdown-color)",
                          }}
                        >
                          <div className="w-16 h-16 rounded-full border border-1 overflow-hidden mr-2">
                            {kam.profilePhoto ? (
                              <img
                                src={kam?.profilePhoto}
                                alt="profilePic"
                                className=" w-full h-full"
                              />
                            ) : (
                              <img
                                src="/assests/AdminDashboard/userForIp.webp"
                                alt="ProfilePhoto"
                              />
                            )}
                          </div>
                          <div className=" flex flex-col items-start justify-center">
                            {CapitalizeFirstLetter(kam.name)}
                            <Chip
                              label={
                                kam?.role == -1
                                  ? "Super Admin"
                                  : kam?.role == 0
                                    ? "CSM"
                                    : kam.role == 1
                                      ? "BD"
                                      : kam.role == 3
                                        ? "Purchase"
                                        : kam.role == 4
                                          ? "Operations"
                                          : kam.role == 7
                                            ? "Designer"
                                            : "Team Member"
                              }
                              color={isSelected ? "primary" : "default"}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                borderRadius: "6px",
                                color: "#fff",
                                width: "fit-content",
                                mt: 0.8, // ✅ chip moves to new line
                                "&.MuiChip-colorDefault": {
                                  backgroundColor: "#9e9e9e",
                                  color: "#fff",
                                },
                              }}
                            />
                          </div>
                          <Checkbox checked={isSelected} sx={{ ml: "auto" }} />
                        </MenuItem>
                      );
                    })}
                </div>
              ) : (
                <div className="px-3 h-[85%] overflow-auto flex items-center justify-center">
                  <p className="text-center text-gray-500 py-4">No KAM found</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>

        {/* Fixed Footer */}
        <DialogActions
          sx={{
            p: 2,
            bottom: 0,
          }}
        >
          <div className="w-30">
            <GlobalButton variant="secondary" size="medium" onClick={onClose}>
              {`Close`}
            </GlobalButton>
          </div>
          {campaignModal ? (
            <div className="w-30">
              <GlobalButton
                variant="primary"
                size="medium"
                disabled={loading || selectedKam.length <= 1}
                isLoading={loading}
                onClick={() => handleCampaignAssign(selectedKam)}
              >
                {`Assign`}
              </GlobalButton>
            </div>
          ) : (
            <div className="w-30">
              <GlobalButton
                disabled={loading || selectedKam?.length == 1}
                isLoading={loading}
                variant="primary"
                size="medium"
                onClick={handleAssign}
              >
                {`Assign`}
              </GlobalButton>
            </div>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KamDropdown;
